import React, {CSSProperties, FC, ReactNode, useState} from 'react';
import clsx from 'clsx';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Typography,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {ReactComponent as AddAssetIcon} from '../../../assets/images/add.svg';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {ReactComponent as AlertIcon} from '../../../assets/images/alert-icon.svg';

import ArrButton from '../arr-button/ArrButton';
import ArrBanner from '../arr-banner/ArrBanner';
import ArrTooltip from '../arr-tooltip/ArrTooltip';
import {ReactComponent as DeleteIcon} from '../../../assets/images/close-base-icon.svg';
import {t} from 'i18next';

import './ArrSectionCard.scss';

export type ArrSectionCardMode = 'edit' | 'view';

export interface ArrSectionCardProps {
  label: ReactNode;
  showInfoIcon?: boolean;
  infoContent?: ReactNode;
  items: any[];
  renderItem: (item: any, index: number) => React.ReactNode;
  /** Optional per-item accordion header (pill + title, etc.). Requires itemAccordion. */
  renderItemHeader?: (item: any, index: number) => React.ReactNode;
  /** When true, each item is its own accordion with renderItemHeader as the summary. */
  itemAccordion?: boolean;
  /** Indexes expanded by default when itemAccordion is enabled. Defaults to [0]. */
  defaultExpandedItemIndexes?: number[];
  accordion?: boolean;
  bodyVariant?: 'default' | 'transparent';
  defaultExpanded?: boolean;
  showAddButton?: boolean;
  showDeleteButton?: boolean;
  addButtonLabel?: string;
  onAdd?: () => void;
  onDelete?: (index: number) => void;
  beforeBody?: React.ReactNode;
  /** View-mode warning banner (e.g. ambiguity callouts) */
  bannerContent?: React.ReactNode;
  mode?: ArrSectionCardMode;
  /** When mode is view, render transparent body items as a bullet list */
  viewAsList?: boolean;
  className?: string;
  dataTestId?: string;
}

export interface ArrSectionRowProps {
  children: ReactNode;
  className?: string;
}

export interface ArrSectionItemProps {
  children: ReactNode;
  className?: string;

  /** Flex grow value */
  flex?: number;

  /** Fixed width */
  width?: string | number;

  /** Optional min width */
  minWidth?: string | number;

  /** Optional max width */
  maxWidth?: string | number;
}

const Row: FC<ArrSectionRowProps> = ({children, className}) => (
  <div className={clsx('arr-section-row', className)}>{children}</div>
);

const Item: FC<ArrSectionItemProps> = ({
  children,
  className,
  flex = 1,
  width,
  minWidth,
  maxWidth,
}) => {
  const style: CSSProperties = {
    flex,
    width,
    minWidth,
    maxWidth,
  };

  return (
    <div style={style} className={clsx('arr-section-item', className)}>
      {children}
    </div>
  );
};

interface ArrSectionCardComponent extends FC<ArrSectionCardProps> {
  Row: typeof Row;
  Item: typeof Item;
}

const isActivationKey = (key: string) => key === 'Enter' || key === ' ';

const ExpandToggle = ({
  expanded,
  className,
  dataTestId,
  onToggle,
}: {
  expanded: boolean;
  className: string;
  dataTestId: string;
  onToggle: () => void;
}) => {
  const activate = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  };

  return (
    <span
      className={className}
      data-testid={dataTestId}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={t(expanded ? 'common.collapse' : 'common.expand')}
      onClick={activate}
      onKeyDown={(event) => {
        if (isActivationKey(event.key)) {
          activate(event);
        }
      }}
    >
      {expanded ? (
        <KeyboardArrowUpIcon aria-hidden />
      ) : (
        <KeyboardArrowDownIcon aria-hidden />
      )}
    </span>
  );
};

const ArrSectionCard: ArrSectionCardComponent = ({
  label,
  showInfoIcon = false,
  items,
  renderItem,
  renderItemHeader,
  itemAccordion = false,
  defaultExpandedItemIndexes,
  infoContent,
  accordion = false,
  defaultExpanded = true,
  showAddButton = true,
  showDeleteButton = true,
  addButtonLabel = t('common.add'),
  onAdd,
  onDelete,
  beforeBody,
  bannerContent,
  mode = 'edit',
  viewAsList = false,
  className,
  dataTestId = 'arr-section-card',
  bodyVariant = 'default',
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isViewMode = mode === 'view';
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>(
    () => {
      const defaults = defaultExpandedItemIndexes ?? [0];
      return items.reduce<Record<number, boolean>>((acc, _, index) => {
        acc[index] = defaults.includes(index);
        return acc;
      }, {});
    }
  );

  const stopHeaderToggle = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const renderInfoIcon = () => {
    const icon = (
      <span
        className="arr-section-card__info-icon"
        aria-label={t('common.moreInformation')}
        role="button"
        tabIndex={0}
        onClick={stopHeaderToggle}
        onMouseDown={stopHeaderToggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.stopPropagation();
          }
        }}
      >
        <InfoIcon aria-hidden />
      </span>
    );

    if (!infoContent) {
      return icon;
    }

    return (
      <span
        className="arr-section-card__info-tooltip"
        onClick={stopHeaderToggle}
        onMouseDown={stopHeaderToggle}
      >
        <ArrTooltip
          content={infoContent}
          placement="top"
          tooltipKey={dataTestId}
        >
          {icon}
        </ArrTooltip>
      </span>
    );
  };

  const renderHeader = () => (
    <div className="arr-section-card__header">
      <div className="arr-section-card__title-wrapper">
        <Typography
          className={clsx(
            'arr-section-card__title',
            'typo-text',
            'subhead4_size'
          )}
        >
          {label}
        </Typography>

        {showInfoIcon && renderInfoIcon()}
      </div>
    </div>
  );

  const renderBanner = () =>
    bannerContent ? (
      <ArrBanner
        type="contextual"
        variant="warning"
        bannerContent={bannerContent}
        startIcon={<AlertIcon />}
        dataTestId={`${dataTestId}-warning-banner`}
        className="arr-section-card__banner"
      />
    ) : null;

  const renderAccordionItems = () =>
    items.map((item, index) => {
      const isExpanded = expandedItems[index] ?? false;
      const itemTestId = `${dataTestId}-item-${index}`;

      return (
        <div
          key={index}
          className="arr-section-card__item-accordion-wrap"
          data-testid={`${itemTestId}-wrap`}
        >
          <Accordion
            disableGutters
            elevation={0}
            expanded={isExpanded}
            onChange={() => undefined}
            className="arr-section-card__item-accordion"
            data-testid={itemTestId}
          >
            <AccordionSummary
              component="div"
              role="presentation"
              tabIndex={-1}
              className="arr-section-card__item-accordion-header"
              data-testid={`${itemTestId}-header`}
            >
              <ExpandToggle
                expanded={isExpanded}
                className="arr-section-card__item-accordion-expand"
                dataTestId={`${itemTestId}-expand`}
                onToggle={() =>
                  setExpandedItems((prev) => ({
                    ...prev,
                    [index]: !prev[index],
                  }))
                }
              />
              <div
                className="arr-section-card__item-accordion-title"
                data-testid={`${itemTestId}-title`}
              >
                {renderItemHeader?.(item, index)}
              </div>
            </AccordionSummary>
            <AccordionDetails
              className="arr-section-card__item-accordion-body"
              data-testid={`${itemTestId}-body`}
            >
              {renderItem(item, index)}
            </AccordionDetails>
          </Accordion>

          {showDeleteButton && (
            <IconButton
              className="arr-section-card__delete-btn"
              onClick={() => onDelete?.(index)}
              size="small"
              aria-label="Delete"
              data-testid={`${itemTestId}-delete`}
            >
              <DeleteIcon aria-hidden="true" />
            </IconButton>
          )}
        </div>
      );
    });

  const renderPlainItems = () =>
    items.map((item, index) => {
      const itemTestId = `${dataTestId}-item-${index}`;

      return (
        <div
          key={index}
          className="arr-section-card__item"
          data-testid={itemTestId}
        >
          <div
            className="arr-section-card__item-content"
            data-testid={`${itemTestId}-content`}
          >
            {renderItem(item, index)}
          </div>

          {showDeleteButton && (
            <IconButton
              className="arr-section-card__delete-btn"
              onClick={() => onDelete?.(index)}
              size="small"
              aria-label="Delete"
              data-testid={`${itemTestId}-delete`}
            >
              <DeleteIcon aria-hidden="true" />
            </IconButton>
          )}
        </div>
      );
    });

  const renderBody = () => (
    <>
      {beforeBody}
      {renderBanner()}
      {items.length > 0 && (
        <div
          className={clsx(
            'arr-section-card__body',
            `arr-section-card__body--${bodyVariant}`,
            isViewMode && 'arr-section-card__body--view',
            isViewMode && viewAsList && 'arr-section-card__body--view-list',
            itemAccordion && 'arr-section-card__body--item-accordion'
          )}
        >
          {itemAccordion ? renderAccordionItems() : renderPlainItems()}
        </div>
      )}

      {showAddButton && (
        <div className="arr-section-card__footer">
          <ArrButton
            variant="text"
            size="medium"
            startIcon={<AddAssetIcon />}
            className="arr-section-card__add-btn"
            dataTestId={`${dataTestId}-add`}
            onClick={onAdd}
            aria-label={
              typeof label === 'string'
                ? `${addButtonLabel} ${label}`
                : addButtonLabel
            }
          >
            {addButtonLabel}
          </ArrButton>
        </div>
      )}
    </>
  );

  if (!accordion) {
    return (
      <div
        className={clsx(
          'arr-section-card',
          isViewMode && 'arr-section-card--view',
          className
        )}
        data-testid={dataTestId}
      >
        {renderHeader()}
        {renderBody()}
      </div>
    );
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={() => undefined}
      className={clsx(
        'arr-section-card',
        'arr-section-card--accordion',
        isViewMode && 'arr-section-card--view',
        className
      )}
      data-testid={dataTestId}
    >
      <AccordionSummary
        component="div"
        role="presentation"
        tabIndex={-1}
        className="arr-section-card__accordion-header"
        expandIcon={
          <ExpandToggle
            expanded={expanded}
            className="arr-section-card__expand-icon"
            dataTestId={`${dataTestId}-expand`}
            onToggle={() => setExpanded((prev) => !prev)}
          />
        }
      >
        {renderHeader()}
      </AccordionSummary>

      <AccordionDetails className="arr-section-card__accordion-body">
        {renderBody()}
      </AccordionDetails>
    </Accordion>
  );
};

ArrSectionCard.Row = Row;
ArrSectionCard.Item = Item;

export default ArrSectionCard;
